import { FC, ReactNode, useContext, useState } from "react";
import type { MouseEvent } from "react";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading, Regular, Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Button, Collapse, Color, DataRow, Icon, Spinner, Tag } from "capitalrx-components-2.0/lib/general";

import { Finance } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import styles from "./MedicalClaimsProviderList.module.scss";
import { TransactionRow } from "./TransactionRow";
import { formatDollars } from "capitalrx-utilities";
import { get835URL, getEOPURL } from "@/api";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { getThemeButton } from "@/utils";

const voidTag = <Tag className={styles.TagStyle} type="system-high-contrast">Issued</Tag>;
const clearedTag = <Tag className={styles.TagStyle} type="success-high-contrast">Cleared</Tag>;

export type UiSortDirection = "ascending" | "descending" | null;

export type SortField = "start_date" | "last_name" | "submission_date" | "status";

type PaymentDropdownProps = {
  payment_date?: string;
  isUpcoming: boolean;
  result_rows: ReadonlyArray<DataRow<Finance.MedicalClaimProviderReportList>>;
  total_results: number;
  transaction?: Finance.MedicalPaymentTransactionSchema[];
  loading: boolean;

  sortField: SortField | null;
  sortDirection: UiSortDirection;
  onSortChange: (field: SortField) => void;
  defaultExpanded?: boolean;
  showDownloadDebugAlert?: boolean;
};

type SortHeaderCellProps = {
  label: ReactNode;
  field: SortField;
  activeField: SortField | null;
  sortDirection: UiSortDirection;
  onSort: (field: SortField) => void;
  align?: "left" | "right";
};

const SortHeaderCell: FC<SortHeaderCellProps> = ({
  label,
  field,
  activeField,
  sortDirection,
  onSort,
  align = "left",
}) => {
  const isActive = activeField === field;

  const directionToShow: UiSortDirection = isActive ? sortDirection : null;

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={styles.SortHeaderButton}
      aria-sort={
        (() => {
          switch (directionToShow) {
            case "ascending":
              return "ascending";
            case "descending":
              return "descending";
            default:
              return "none";
          }
        })()
      }
      style={{ width: "100%" }}
    >
      <Shelf spacing="mini">
        <Regular className={styles.TitleText}>{label}</Regular>

        <>
          {directionToShow === "ascending" && (
            <Icon
              className="sort-indicator"
              color={Color.Turqoise[500]}
              name="arrow-down"
              size="small"
              aria-hidden="true"
            />
          )}

          {directionToShow === "descending" && (
            <Icon
              className="sort-indicator"
              color={Color.Turqoise[500]}
              name="arrow-up"
              size="small"
              aria-hidden="true"
            />
          )}

          {directionToShow === null && (
            <Icon
              className="sort-indicator unsorted"
              name="arrow-down"
              size="small"
              aria-hidden="true"
            />
          )}
        </>
        <Shelf.Spacer />
      </Shelf>
    </button>
  );
};

export const PaymentDropdown: FC<PaymentDropdownProps> = ({
  payment_date,
  isUpcoming,
  result_rows,
  total_results,
  transaction,
  loading,
  sortField,
  sortDirection,
  onSortChange,
  defaultExpanded = isUpcoming,
  showDownloadDebugAlert = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { isCompressed } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const StackOrShelf = isCompressed ? Stack : Shelf;
  const { getAuthToken } = useContext(AuthenticationContext);
  const authToken = getAuthToken();
  const { primary_color } = useContext(ThemeSelectorContext);

  const handleEopClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (showDownloadDebugAlert) {
      alert("EOP download clicked with transaction ID: " + transaction?.[0]?.transaction_id);
    }
    getEOPURL({ transaction_ids: [transaction?.[0]?.transaction_id] }, authToken);
  };

  const handle835Click = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (showDownloadDebugAlert) {
      alert("835 download clicked with transaction ID: " + transaction?.[0]?.medical_835_record_id);
    }
    get835URL({ id: Number(transaction?.[0]?.medical_835_record_id) }, authToken);
  };

  const title = isUpcoming ? (
    <StackOrShelf spacing="mini">
      <Subheading>Upcoming Payments</Subheading>
      {!isCompressed && <StackOrShelf.Spacer />}
      <Regular className={styles.TitleText}>
        <span className={styles.Number}>{total_results}</span>
        {total_results === 1 ? " claim" : " claims"}
      </Regular>
    </StackOrShelf>
  ) : (
    <>
      {isCompressed && (
        <Stack spacing="mini" alignment="left">
          <Subheading>Payment Issued: {payment_date ?? "Unscheduled"}</Subheading>
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_void && voidTag}
          {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_cleared && clearedTag}
          <Shelf spacing="mini">
            <Semibold>Transaction ID: </Semibold>
            <Body>#{transaction?.[0]?.transaction_id}</Body>
          </Shelf>
          <Shelf spacing="mini">
            <Semibold>Total Paid: </Semibold>
            <Body>{formatDollars(transaction?.[0]?.funding_ledger_amount)}</Body>
          </Shelf>
          <Stack alignment="left">
            <Shelf spacing="mini">
              <Semibold>Type: </Semibold>
              <Body>{transaction?.[0]?.funding_ledger_type}</Body>
            </Shelf>
          </Stack>
          <StackOrShelf.Spacer />
          <Regular className={styles.TitleText}>
            <span className={styles.Number}>{total_results}</span>
            {total_results === 1 ? " claim" : " claims"}
          </Regular>
          <Button icon="download" appearance={getThemeButton("tertiary", primary_color)} onClick={handleEopClick}>
            PDF
          </Button>
          <Button icon="download" appearance={getThemeButton("tertiary", primary_color)} onClick={handle835Click}>
            835
          </Button>
        </Stack>
      )}
      {
        !isCompressed && (
          <Shelf>
            <Subheading>Payment Issued: {payment_date ?? "Unscheduled"}</Subheading>
            {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_void && voidTag}
            {result_rows?.[0]?.data.provider_medical_claim_payment_report.payment_is_cleared && clearedTag}
            <Shelf spacing="mini">
              <Semibold>Transaction ID: </Semibold>
              <Body>#{transaction?.[0]?.transaction_id}</Body>
            </Shelf>
            <Shelf spacing="mini">
              <Semibold>Total Paid: </Semibold>
              <Body>{formatDollars(transaction?.[0]?.funding_ledger_amount)}</Body>
            </Shelf>
            <Shelf spacing="mini">
              <Semibold>Type: </Semibold>
              <Body>{transaction?.[0]?.funding_ledger_type}</Body>
            </Shelf>
            <StackOrShelf.Spacer />
            <Regular className={styles.TitleText}>
              <span className={styles.Number}>{total_results}</span>
              {total_results === 1 ? " claim" : " claims"}
            </Regular>
            <Button icon="download" appearance={getThemeButton("tertiary", primary_color)} onClick={handleEopClick}>
              PDF
            </Button>
            <Button icon="download" appearance={getThemeButton("tertiary", primary_color)} onClick={handle835Click}>
              835
            </Button>
          </Shelf>
        )
      }
    </>
  );

  return (
    <Stack spacing="small">
      <Collapse.Blank.Unpadded
        alignCaret={isCompressed ? "right" : "left"}
        title={title}
        expanded={expanded}
        onToggleExpand={() => setExpanded((e) => !e)}
        style={{ padding: 0 }}
        className={styles.CollapseNoPadding}
      >
        {loading && (
          <Stack className={styles.LoadingContainer} alignment="center">
            <Spinner size="large" />
          </Stack>
        )}
        {isCompressed && <div className={isCompressed ? styles.ClaimRow : ""} />}

        {!loading && (
          <Stack style={{ paddingTop: "8px" }}>
            {total_results === 0 && (
              <Shelf>
                <Body>No payments found</Body>
              </Shelf>
            )}
            {total_results > 0 && (
              <Stack spacing="small">
                {!isCompressed && (
                  <Shelf spacing="medium-large" className={styles.TitleRow}>
                    <Stack alignment="left" spacing="mini" style={{ width: "115px" }}>
                      <SortHeaderCell
                        label="service start"
                        field="start_date"
                        activeField={sortField}
                        sortDirection={sortDirection}
                        onSort={onSortChange}
                      />
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>member id</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <SortHeaderCell
                        label="patient name"
                        field="last_name"
                        activeField={sortField}
                        sortDirection={sortDirection}
                        onSort={onSortChange}
                      />
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>
                        patient account <span className={styles.Number}>#</span>
                      </Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "179px" }}>
                      <SortHeaderCell
                        label="submission date"
                        field="submission_date"
                        activeField={sortField}
                        sortDirection={sortDirection}
                        onSort={onSortChange}
                      />
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="left" spacing="mini" style={{ width: "108px" }}>
                      <SortHeaderCell
                        label="claim status"
                        field="status"
                        activeField={sortField}
                        sortDirection={sortDirection}
                        onSort={onSortChange}
                      />
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="right" spacing="mini" style={{ width: "75px" }}>
                      <Regular className={styles.TitleText}>amount paid</Regular>
                    </Stack>
                    <Shelf.Spacer />
                    <Stack alignment="right" spacing="mini" style={{ width: "179px" }}>
                      <Regular className={styles.TitleText}>patient responsibility</Regular>
                    </Stack>
                  </Shelf>
                )}

                {/* Data rows */}
                {result_rows.map((row) => (
                  <StackOrShelf spacing="small" key={row.id}>
                    <TransactionRow row={row.data.provider_medical_claim_payment_report} />
                  </StackOrShelf>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Collapse.Blank.Unpadded>
      {!expanded && isCompressed && <div className={styles.ClaimRow} />}
    </Stack>
  );
};
